/**
 * User menu component - dropdown menu for user actions
 */

import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { UserProfile } from './UserProfile';
import { ThemeSwitcher } from './ThemeSwitcher';

interface UserMenuProps {
  onNavigateToAdmin: () => void;
}

export function UserMenu({ onNavigateToAdmin }: UserMenuProps) {
  const { user, logout } = useAuth();
  const [showProfile, setShowProfile] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showThemeMenu, setShowThemeMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleLogout = () => {
    logout();
  };

  const handleThemeClick = () => {
    setShowThemeMenu(true);
  };

  const handleBackToMenu = () => {
    setShowThemeMenu(false);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
        setShowThemeMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  return (
    <>
      <div className="user-menu-section" ref={menuRef}>
        <div
          className="user-menu-trigger"
          onClick={() => {
            setShowUserMenu(!showUserMenu);
            setShowThemeMenu(false);
          }}
        >
          <span className="username">{user?.display_name}</span>
          <span className="menu-arrow">{showUserMenu ? '▲' : '▼'}</span>
        </div>

        {showUserMenu && !showThemeMenu && (
          <div className="user-dropdown">
            <div
              className="dropdown-item"
              onClick={handleThemeClick}
            >
              THEME
            </div>
            <div
              className="dropdown-item"
              onClick={() => {
                setShowProfile(true);
                setShowUserMenu(false);
              }}
            >
              PROFILE
            </div>
            {user?.roles.includes('admin') && (
              <div
                className="dropdown-item"
                onClick={() => {
                  onNavigateToAdmin();
                  setShowUserMenu(false);
                }}
              >
                ADMIN
              </div>
            )}
            <div className="dropdown-item" onClick={handleLogout}>
              LOGOUT
            </div>
          </div>
        )}

        {showUserMenu && showThemeMenu && (
          <div className="user-dropdown theme-dropdown">
            <div
              className="dropdown-item dropdown-back"
              onClick={handleBackToMenu}
            >
              ← BACK
            </div>
            <ThemeSwitcher
              alwaysOpen={true}
            />
          </div>
        )}
      </div>
      {showProfile && <UserProfile onClose={() => setShowProfile(false)} />}
    </>
  );
}
